import { useState } from 'react';

// Pull Credit Modal
export const PullCreditModal = ({ show, onClose, onSubmit, borrowerName }) => {
  const [creditType, setCreditType] = useState('hard');
  const [borrowerScope, setBorrowerScope] = useState('individual');
  const [isReissue, setIsReissue] = useState(false);
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      creditType,
      borrowerScope,
      isReissue,
      reason,
      timestamp: new Date().toISOString()
    });
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-credit-card me-2"></i>
              Pull Credit Report
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <i className="ti ti-info-circle me-2"></i>
                Pulling credit for: <strong>{borrowerName}</strong>
              </div>

              <div className="mb-3">
                <label className="form-label required">Credit Type</label>
                <select 
                  className="form-select" 
                  value={creditType} 
                  onChange={(e) => setCreditType(e.target.value)}
                  required
                >
                  <option value="soft">Soft Pull (No impact on score)</option>
                  <option value="hard">Hard Pull (Standard inquiry)</option>
                </select>
                <small className="form-hint">
                  Soft pulls do not affect credit score. Hard pulls are required for final approval.
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label required">Borrower Scope</label>
                <select 
                  className="form-select" 
                  value={borrowerScope} 
                  onChange={(e) => setBorrowerScope(e.target.value)}
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="joint">Joint (All borrowers)</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input 
                    type="checkbox" 
                    className="form-check-input" 
                    checked={isReissue}
                    onChange={(e) => setIsReissue(e.target.checked)}
                  />
                  <span className="form-check-label">This is a re-issue</span>
                </label>
                <small className="form-hint">
                  Check if this replaces a previous credit pull
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Reason for Pull (Optional)</label>
                <textarea 
                  className="form-control" 
                  rows="2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Initial application, rate refinance, etc."
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="ti ti-download me-1"></i>
                Pull Credit Report
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Order Appraisal Modal
export const OrderAppraisalModal = ({ show, onClose, onSubmit, property }) => {
  const [appraisalType, setAppraisalType] = useState('full');
  const [propertyType, setPropertyType] = useState(property?.propertyType || 'Single Family');
  const [occupancy, setOccupancy] = useState(property?.occupancy || 'Primary Residence');
  const [estimatedValue, setEstimatedValue] = useState(property?.estimatedValue || '');
  const [loanType, setLoanType] = useState('Conventional');
  const [rushOrder, setRushOrder] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      appraisalType,
      propertyType,
      occupancy,
      estimatedValue: parseFloat(estimatedValue),
      loanType,
      rushOrder,
      timestamp: new Date().toISOString()
    });
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-home-search me-2"></i>
              Order Appraisal
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <i className="ti ti-map-pin me-2"></i>
                Property: <strong>{property?.address || 'Address not set'}</strong>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label required">Appraisal Type</label>
                  <select 
                    className="form-select" 
                    value={appraisalType} 
                    onChange={(e) => setAppraisalType(e.target.value)}
                    required
                  >
                    <option value="full">Full Appraisal (Interior & Exterior)</option>
                    <option value="drive-by">Drive-By (Exterior Only)</option>
                    <option value="desktop">Desktop Appraisal</option>
                    <option value="waiver">Appraisal Waiver (If eligible)</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label required">Property Type</label>
                  <select 
                    className="form-select" 
                    value={propertyType} 
                    onChange={(e) => setPropertyType(e.target.value)}
                    required
                  >
                    <option value="Single Family">Single Family</option>
                    <option value="Condo">Condo</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Multi-Family">Multi-Family (2-4 units)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label required">Occupancy</label>
                  <select 
                    className="form-select" 
                    value={occupancy} 
                    onChange={(e) => setOccupancy(e.target.value)}
                    required
                  >
                    <option value="Primary Residence">Primary Residence</option>
                    <option value="Second Home">Second Home</option>
                    <option value="Investment">Investment Property</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label required">Estimated Value</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={estimatedValue}
                      onChange={(e) => setEstimatedValue(e.target.value)}
                      required
                      min="0"
                      step="1000"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label required">Loan Type</label>
                <select 
                  className="form-select" 
                  value={loanType} 
                  onChange={(e) => setLoanType(e.target.value)}
                  required
                >
                  <option value="Conventional">Conventional</option>
                  <option value="FHA">FHA</option>
                  <option value="VA">VA</option>
                  <option value="USDA">USDA</option>
                  <option value="Jumbo">Jumbo</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input 
                    type="checkbox" 
                    className="form-check-input" 
                    checked={rushOrder}
                    onChange={(e) => setRushOrder(e.target.checked)}
                  />
                  <span className="form-check-label">Rush Order (+$150 fee, 3-5 day turnaround)</span>
                </label>
              </div>

              <div className="alert alert-warning mb-0">
                <strong>Estimated Cost:</strong> ${rushOrder ? 700 : 550}
                <br />
                <small>Final cost may vary based on property complexity and location</small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="ti ti-send me-1"></i>
                Order Appraisal
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Open Title Modal
export const OpenTitleModal = ({ show, onClose, onSubmit, property, borrower }) => {
  const [transactionType, setTransactionType] = useState('purchase');
  const [titleCompany, setTitleCompany] = useState('');
  const [rushOrder, setRushOrder] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      transactionType,
      titleCompany,
      rushOrder,
      timestamp: new Date().toISOString()
    });
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-file-certificate me-2"></i>
              Open Title Order
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <div><strong>Property:</strong> {property?.address || 'Address not set'}</div>
                <div><strong>Borrower:</strong> {borrower?.name || 'Not set'}</div>
              </div>

              <div className="mb-3">
                <label className="form-label required">Transaction Type</label>
                <select 
                  className="form-select" 
                  value={transactionType} 
                  onChange={(e) => setTransactionType(e.target.value)}
                  required
                >
                  <option value="purchase">Purchase</option>
                  <option value="refinance">Refinance</option>
                  <option value="cash-out-refinance">Cash-Out Refinance</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Preferred Title Company (Optional)</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={titleCompany}
                  onChange={(e) => setTitleCompany(e.target.value)}
                  placeholder="Leave blank to use default provider"
                />
                <small className="form-hint">
                  If left blank, we'll assign a title company from our network
                </small>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input 
                    type="checkbox" 
                    className="form-check-input" 
                    checked={rushOrder}
                    onChange={(e) => setRushOrder(e.target.checked)}
                  />
                  <span className="form-check-label">Rush Order (5-7 day turnaround)</span>
                </label>
              </div>

              <div className="alert alert-warning mb-0">
                <strong>Note:</strong> Title company will contact you within 1 business day to confirm order details and schedule closing.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="ti ti-send me-1"></i>
                Open Title Order
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Order VOA (Verification of Assets) Modal
export const OrderVOAModal = ({ show, onClose, onSubmit, borrowerName }) => {
  const [verificationScope, setVerificationScope] = useState('all');
  const [accountTypes, setAccountTypes] = useState(['checking', 'savings']);

  const handleAccountTypeToggle = (type) => {
    setAccountTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      verificationScope,
      accountTypes,
      timestamp: new Date().toISOString()
    });
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-building-bank me-2"></i>
              Order Digital Asset Verification (VOA)
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <i className="ti ti-info-circle me-2"></i>
                Ordering VOA for: <strong>{borrowerName}</strong>
              </div>

              <div className="mb-3">
                <label className="form-label required">Verification Scope</label>
                <select 
                  className="form-select" 
                  value={verificationScope} 
                  onChange={(e) => setVerificationScope(e.target.value)}
                  required
                >
                  <option value="all">All Accounts</option>
                  <option value="selected">Selected Account Types</option>
                </select>
              </div>

              {verificationScope === 'selected' && (
                <div className="mb-3">
                  <label className="form-label">Account Types to Verify</label>
                  <div>
                    <label className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={accountTypes.includes('checking')}
                        onChange={() => handleAccountTypeToggle('checking')}
                      />
                      <span className="form-check-label">Checking Accounts</span>
                    </label>
                    <label className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={accountTypes.includes('savings')}
                        onChange={() => handleAccountTypeToggle('savings')}
                      />
                      <span className="form-check-label">Savings Accounts</span>
                    </label>
                    <label className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={accountTypes.includes('investment')}
                        onChange={() => handleAccountTypeToggle('investment')}
                      />
                      <span className="form-check-label">Investment Accounts</span>
                    </label>
                    <label className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={accountTypes.includes('retirement')}
                        onChange={() => handleAccountTypeToggle('retirement')}
                      />
                      <span className="form-check-label">Retirement Accounts (401k, IRA)</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="alert alert-success mb-0">
                <strong>Digital Verification Benefits:</strong>
                <ul className="mb-0 mt-2">
                  <li>Instant results (typically within minutes)</li>
                  <li>No manual document upload required</li>
                  <li>90-day transaction history included</li>
                  <li>Borrower connects directly via secure portal</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="ti ti-send me-1"></i>
                Order VOA
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Order VOIE (Verification of Income/Employment) Modal
export const OrderVOIEModal = ({ show, onClose, onSubmit, borrowerName, employer }) => {
  const [verificationType, setVerificationType] = useState('both');
  const [employerName, setEmployerName] = useState(employer || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      verificationType,
      employerName,
      timestamp: new Date().toISOString()
    });
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-briefcase me-2"></i>
              Order Digital Income/Employment Verification (VOIE)
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info mb-3">
                <i className="ti ti-info-circle me-2"></i>
                Ordering VOIE for: <strong>{borrowerName}</strong>
              </div>

              <div className="mb-3">
                <label className="form-label required">Employer Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={employerName}
                  onChange={(e) => setEmployerName(e.target.value)}
                  placeholder="Enter employer name"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label required">Verification Type</label>
                <select 
                  className="form-select" 
                  value={verificationType} 
                  onChange={(e) => setVerificationType(e.target.value)}
                  required
                >
                  <option value="both">Income & Employment</option>
                  <option value="income">Income Only</option>
                  <option value="employment">Employment Only</option>
                </select>
              </div>

              <div className="alert alert-success mb-0">
                <strong>Digital Verification Benefits:</strong>
                <ul className="mb-0 mt-2">
                  <li>Instant results (typically within minutes)</li>
                  <li>No manual paystub upload required</li>
                  <li>24-month income history included</li>
                  <li>Employment status verified in real-time</li>
                  <li>Borrower connects via secure payroll portal</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="ti ti-send me-1"></i>
                Order VOIE
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
